import { useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { filterByWindow } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

export default function GPSMapWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimelineControls();
  const recId = widgetConfig?.recId || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec = recordings.find((r) => r.id === recId);

  const hasGPS = useMemo(() => {
    if (!rec?.data) return false;
    const keys = Object.keys(rec.data);
    return keys.some((k) => k.toLowerCase().endsWith('longitude')) && keys.some((k) => k.toLowerCase().endsWith('latitude'));
  }, [rec]);

  const mapData = useMemo(() => {
    if (!rec?.data || !hasGPS) return null;
    const lonKey = Object.keys(rec.data).find((k) => k.toLowerCase().endsWith('longitude'));
    const latKey = Object.keys(rec.data).find((k) => k.toLowerCase().endsWith('latitude'));
    if (!lonKey || !latKey) return null;

    let lonPoints = rec.data[lonKey];
    let latPoints = rec.data[latKey];
    let sensorPoints = sensorKey ? rec.data[sensorKey] : null;

    if (timeWindow) {
      lonPoints = filterByWindow(lonPoints, timeWindow.start, timeWindow.end);
      latPoints = filterByWindow(latPoints, timeWindow.start, timeWindow.end);
      if (sensorPoints) sensorPoints = filterByWindow(sensorPoints, timeWindow.start, timeWindow.end);
    }

    const n = Math.min(lonPoints.length, latPoints.length);
    const coords = [];
    for (let i = 0; i < n; i++) {
      const lat = Number(latPoints[i].value);
      const lon = Number(lonPoints[i].value);
      if (Number.isNaN(lat) || Number.isNaN(lon) || lat === 0 || lon === 0) continue;
      const sensorVal = sensorPoints?.[i] ? Number(sensorPoints[i].value) : null;
      coords.push({ lat, lon, value: sensorVal, time: lonPoints[i].time });
    }
    return coords;
  }, [rec, hasGPS, sensorKey, timeWindow]);

  const sensorName = friendlyNameConverter(sensorKey || '');

  // Simple SVG path map (no heavy map dependency)
  const renderMiniMap = () => {
    if (!mapData || mapData.length === 0) return <Typography variant="body2" color="text.secondary">No GPS data</Typography>;

    const lats = mapData.map((c) => c.lat);
    const lons = mapData.map((c) => c.lon);
    const minLat = Math.min(...lats); const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons); const maxLon = Math.max(...lons);
    const rangeLat = maxLat - minLat || 0.001;
    const rangeLon = maxLon - minLon || 0.001;
    const pad = 20;
    const w = 300; const h = 200;

    const vals = mapData.map((c) => c.value).filter((v) => v !== null);
    const minVal = vals.length > 0 ? Math.min(...vals) : 0;
    const maxVal = vals.length > 0 ? Math.max(...vals) : 1;
    const rangeVal = maxVal - minVal || 1;

    const getColor = (v) => {
      if (v === null) return '#999';
      const t = (v - minVal) / rangeVal;
      const r = Math.round(t * 255);
      const g = Math.round((1 - t) * 255);
      return `rgb(${r},${g},0)`;
    };

    return (
      <svg viewBox={`0 0 ${w + pad * 2} ${h + pad * 2}`} style={{ width: '100%', height: '100%' }}>
        {mapData.map((c, i) => {
          const x = pad + ((c.lon - minLon) / rangeLon) * w;
          const y = pad + (1 - (c.lat - minLat) / rangeLat) * h;
          return (
            <circle key={i} cx={x} cy={y} r={3} fill={getColor(c.value)} opacity={0.7}>
              <title>{`${c.lat.toFixed(5)}, ${c.lon.toFixed(5)}${c.value !== null ? ` — ${sensorName}: ${c.value.toFixed(2)}` : ''}`}</title>
            </circle>
          );
        })}
      </svg>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {sensorKey && mapData && mapData.some((c) => c.value !== null) && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1, pt: 0.5 }}>
          <Box sx={{ width: 60, height: 8, background: 'linear-gradient(to right, rgb(0,255,0), rgb(255,0,0))', borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Low → High</Typography>
        </Stack>
      )}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', p: 1 }}>
        {hasGPS ? renderMiniMap() : (
          <Typography variant="body2" color="text.secondary">
            {rec ? 'No GPS data in this recording' : 'Select a recording'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
